import React, { useState, useEffect } from "react";
import {
  Card,
  Badge,
  Spinner,
  Alert,
  Button,
  Dropdown,
  Row,
  Col,
  ButtonGroup,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import notificationApi from "@/api/notificationApi";
import "./NotificationsList.css";

const NotificationsList = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [page, filter]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: 20,
      };

      if (filter === "unread") params.is_read = false;
      if (filter === "read") params.is_read = true;

      const response = await notificationApi.getNotifications(params);
      const data = response.data || response;

      setNotifications(data.notifications || []);
      setTotalPages(Math.ceil((data.total || 0) / (data.page_size || 20)));
      if (data.unread_count !== undefined) {
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);

      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.warn("Authentication required. Redirecting to login...");
      } else {
        console.error("Failed to fetch notifications:", error.message);
      }

      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      const data = response.data || response;
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching unread count:", error);
      setUnreadCount(0);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleDelete = async (notificationId, event) => {
    event.stopPropagation();
    if (!window.confirm("Bildirishnomani o'chirmoqchimisiz?")) return;

    try {
      await notificationApi.deleteNotification(notificationId);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm("Barcha bildirishnomalarni o'chirmoqchimisiz?")) return;

    try {
      await notificationApi.clearAll();
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      task_created: "📋",
      task_assigned: "👤",
      task_status_changed: "🔄",
      task_deadline_reminder: "⏰",
      task_approved: "✅",
      task_rejected: "❌",
      event_created: "📅",
      event_updated: "📝",
      event_reminder: "🔔",
      admin_broadcast: "📢",
    };
    return icons[type] || "📄";
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      task_created: "Yangi vazifa",
      task_assigned: "Vazifa biriktirildi",
      task_status_changed: "Holat o'zgartirildi",
      task_deadline_reminder: "Muddat eslatmasi",
      task_approved: "Vazifa tasdiqlandi",
      task_rejected: "Vazifa rad etildi",
      event_created: "Yangi tadbir",
      event_updated: "Tadbir o'zgartirildi",
      event_reminder: "Tadbir eslatmasi",
      admin_broadcast: "Admin e'loni",
    };
    return labels[type] || type;
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read_at) {
      handleMarkAsRead(notification.id);
    }

    if (notification.entity_type === "task" && notification.entity_id) {
      navigate(`/tasks/detail/${notification.entity_id}`);
    } else if (notification.entity_type === "event" && notification.entity_id) {
      navigate(`/events/detail/${notification.entity_id}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString("uz-UZ", {
        day: "numeric",
        month: "short",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      });
    }
    if (days > 0) return `${days} kun oldin`;
    if (hours > 0) return `${hours} soat oldin`;
    if (minutes > 0) return `${minutes} daqiqa oldin`;
    return "Hozir";
  };

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-3">
            <h4 className="notifications-title mb-0">
              <i className="fas fa-bell me-2"></i>
              Bildirishnomalar
            </h4>
            {unreadCount > 0 && (
              <Badge bg="danger" pill>
                {unreadCount} yangi
              </Badge>
            )}
          </div>
          <Dropdown>
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              className="notification-menu-btn"
            >
              <i className="fas fa-ellipsis-v"></i>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" className="notification-dropdown-menu">
              <Dropdown.Item
                onClick={handleMarkAllAsRead}
                className="notification-dropdown-item"
              >
                <i className="fas fa-check-double me-2 text-success"></i>
                <span>Hammasini o'qilgan qilish</span>
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={handleClearAll}
                className="notification-dropdown-item text-danger"
              >
                <i className="fas fa-trash-alt me-2"></i>
                <span>Hammasini o'chirish</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="notification-tabs mb-3">
          <ButtonGroup size="sm">
            <Button
              variant={filter === "all" ? "primary" : "outline-secondary"}
              onClick={() => setFilter("all")}
            >
              <i className="fas fa-inbox me-1"></i>
              Hammasi
            </Button>
            <Button
              variant={filter === "unread" ? "primary" : "outline-secondary"}
              onClick={() => setFilter("unread")}
            >
              <i className="fas fa-envelope me-1"></i>
              O'qilmaganlar
            </Button>
            <Button
              variant={filter === "read" ? "primary" : "outline-secondary"}
              onClick={() => setFilter("read")}
            >
              <i className="fas fa-envelope-open me-1"></i>
              O'qilganlar
            </Button>
          </ButtonGroup>
        </div>
      </div>

      <Card className="notifications-card border-0">
        <Card.Body className="p-0">
          {loading ? (
            <div className="loading-container text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="text-muted mt-3">Yuklanmoqda...</p>
            </div>
          ) : !notifications || notifications.length === 0 ? (
            <div className="empty-state text-center py-5">
              <div className="mb-3" style={{ fontSize: "48px", opacity: 0.3 }}>📭</div>
              <p className="text-muted">
                {filter === "unread"
                  ? "O'qilmagan bildirishnomalar yo'q"
                  : filter === "read"
                  ? "O'qilgan bildirishnomalar yo'q"
                  : "Bildirishnomalar yo'q"}
              </p>
            </div>
          ) : (
            <div className="notifications-list-simple">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-list-item ${!notification.read_at ? "unread" : ""}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-type-icon">
                    <span style={{ fontSize: "20px" }}>
                      {getNotificationIcon(notification.notification_type)}
                    </span>
                  </div>

                  <div className="notification-content-simple">
                    <div className="notification-type-label">
                      {getNotificationTypeLabel(notification.notification_type)}
                    </div>
                    <div className="notification-text">
                      {notification.title}
                      {notification.message && (
                        <span className="notification-message-preview">
                          {" — " + notification.message}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="notification-date">
                    {formatDate(notification.sent_at)}
                  </div>

                  {!notification.read_at && <div className="notification-unread-dot"></div>}

                  <div className="notification-delete">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0"
                      onClick={(e) => handleDelete(notification.id, e)}
                      title="O'chirish"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="notifications-pagination p-3 border-top">
              <div className="d-flex justify-content-between align-items-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <i className="fas fa-chevron-left me-1"></i>
                  Oldingi
                </Button>

                <span className="page-info">
                  {page}-sahifa / {totalPages}
                </span>

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page === totalPages}
                >
                  Keyingi
                  <i className="fas fa-chevron-right ms-1"></i>
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default NotificationsList;
